/**
 * F-505-FE-01 (scaffolding) + F-506 (page wiring): Application Router
 *
 * Three route groups with dedicated layouts:
 *
 * 1. DOCTOR APP (DoctorLayout)
 *    /register/:hospitalSlug     — Doctor registration (PUBLIC)
 *    /login/:hospitalSlug        — Doctor login       (PUBLIC)
 *    /dashboard                  — Doctor dashboard
 *    /session/new                — Pre-session setup
 *    /session/:id                — Active session
 *    /session/:id/end            — Session end
 *    /sessions                   — Session history
 *    /notes/*                    — Note review (existing F-404 routes)
 *
 * 2. PATIENT INTERFACE (PatientLayout)
 *    /join                       — QR scan / 6-digit code entry
 *    /session/:id/disclaimer     — Consent / disclaimer
 *    /session/:id/patient        — Active patient session
 *
 * 3. ADMIN PORTAL (AdminLayout)
 *    /admin/login                — Admin login (no layout wrapper)
 *    /admin/dashboard            — Admin dashboard
 *    /admin/languages            — Language management
 *    /admin/specializations      — Specialization management
 *    /admin/templates            — Template management
 *    /admin/*                    — Catch-all → /admin/dashboard
 *
 * Route guards:
 * - <RequireAuth role="doctor"> wraps all doctor authenticated routes
 * - <RequireAuth role="admin"> wraps all admin authenticated routes
 * - Patient routes are unauthenticated (session token via QR)
 *
 * Hospital finder: bare /login and /register (without :hospitalSlug) display
 * HospitalFinderPage — prompts for hospital code before navigating to slug route.
 */

import { type ReactNode } from 'react';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import { DoctorLayout } from '@/layouts/DoctorLayout';
import { NotificationToast } from '@/components/NotificationToast';
import { PatientLayout } from '@/layouts/PatientLayout';
import { AdminLayout } from '@/layouts/AdminLayout';

// F-404: Existing note pages
import { NoteDecisionPage } from '@/features/notes/NoteDecisionPage';
import { NoteEditorPage } from '@/features/notes/NoteEditorPage';
import { PendingNotesPage } from '@/features/notes/PendingNotesPage';
import { CompletedNotesPage } from '@/features/notes/CompletedNotesPage';

// F-506: Doctor app pages
import { RegisterPage } from '@/features/auth/RegisterPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { HospitalFinderPage } from '@/features/auth/HospitalFinderPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { PreSessionSetup } from '@/features/session/PreSessionSetup';
import { ActiveSessionPage } from '@/features/session/ActiveSessionPage';
import { SessionEndPage } from '@/features/session/SessionEndPage';
import { SessionHistoryPage } from '@/features/session/SessionHistoryPage';

// F-507: Patient interface pages
import { JoinPage } from '@/features/patient/JoinPage';
import { DisclaimerPage } from '@/features/patient/DisclaimerPage';
import { PatientSessionPage } from '@/features/patient/PatientSessionPage';

// P2.5: MFA pages
import { MfaSetupPage } from '@/features/auth/MfaSetupPage';
import { MfaVerifyPage } from '@/features/auth/MfaVerifyPage';

// Password reset pages (public)
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';

// F-508: Admin portal pages
import { AdminLoginPage as AdminLoginPageFull } from '@/features/admin/AdminLoginPage';
import { AdminMfaVerifyPage } from '@/features/admin/AdminMfaVerifyPage';
import { AdminMfaSetupPage } from '@/features/admin/AdminMfaSetupPage';
import { AdminMfaResetRequestPage, AdminMfaResetConfirmPage } from '@/features/admin/AdminMfaResetPage';
import { AdminDashboardPage } from '@/features/admin/AdminDashboardPage';
import { HospitalDetailPage } from '@/features/admin/HospitalDetailPage';
import { LanguageManagement } from '@/features/admin/LanguageManagement';
import { SpecializationManagement } from '@/features/admin/SpecializationManagement';
import { SpecializationDetailPage } from '@/features/admin/SpecializationDetailPage';
import { TemplateManagement } from '@/features/admin/TemplateManagement';
import { PlatformSettingsPage } from '@/features/admin/PlatformSettingsPage';

// ---------------------------------------------------------------------------
// Route guard
// ---------------------------------------------------------------------------

interface RequireAuthProps {
  role: 'doctor' | 'admin';
  redirectTo?: string;
  children?: ReactNode;
}

function RequireAuth({ role, redirectTo, children }: RequireAuthProps) {
  const { isAuthenticated, isLoading, role: userRole } = useAuth();

  if (isLoading) {
    // Brief spinner while session is being restored from cookie
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-clinical-amber border-t-transparent rounded-full animate-spin" />
          <p className="text-sm font-body text-clinical-text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || userRole !== role) {
    const fallback =
      redirectTo ?? (role === 'admin' ? '/admin/login' : '/login');
    return <Navigate to={fallback} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}

// ---------------------------------------------------------------------------
// Doctor layout wrapper for authenticated routes
// ---------------------------------------------------------------------------

function DoctorLayoutOutlet() {
  return (
    <DoctorLayout>
      <Outlet />
    </DoctorLayout>
  );
}

// ---------------------------------------------------------------------------
// Admin layout wrapper for authenticated routes
// ---------------------------------------------------------------------------

function AdminLayoutOutlet() {
  return (
    <AdminLayout>
      <Outlet />
    </AdminLayout>
  );
}

// ---------------------------------------------------------------------------
// Patient layout wrapper
// ---------------------------------------------------------------------------

function PatientLayoutOutlet() {
  return (
    <PatientLayout>
      <Outlet />
    </PatientLayout>
  );
}

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export function AppRouter() {
  return (
    <BrowserRouter>
      {/* F-604: Real-time notification toasts — rendered globally inside Router */}
      <NotificationToast />
      <Routes>
        {/* ================================================================ */}
        {/* Root redirect                                                     */}
        {/* ================================================================ */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* ================================================================ */}
        {/* DOCTOR — unauthenticated public routes                           */}
        {/* Register and Login do NOT require auth.                          */}
        {/* ================================================================ */}

        {/* Registration routes */}
        <Route
          path="/register"
          element={<HospitalFinderPage mode="register" />}
        />
        <Route
          path="/register/:hospitalSlug"
          element={<RegisterPage />}
        />

        {/* Login routes */}
        <Route
          path="/login"
          element={<HospitalFinderPage mode="login" />}
        />
        <Route
          path="/login/:hospitalSlug"
          element={<LoginPage />}
        />

        {/* Password reset public pages */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* ================================================================ */}
        {/* DOCTOR — authenticated, with DoctorLayout from router             */}
        {/* Dashboard, session pages, and session history get DoctorLayout   */}
        {/* ================================================================ */}
        <Route element={<RequireAuth role="doctor" />}>
          <Route element={<DoctorLayoutOutlet />}>
            {/* Dashboard */}
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* Session history */}
            <Route path="/sessions" element={<SessionHistoryPage />} />

            {/* Pre-session setup */}
            <Route path="/session/new" element={<PreSessionSetup />} />

            {/* Active session — full-screen (DoctorLayout provides back button) */}
            <Route path="/session/:id" element={<ActiveSessionPage />} />
          </Route>

          {/*
           * Session end page: full-screen, no DoctorLayout scrolling chrome.
           * Inside RequireAuth but outside DoctorLayoutOutlet.
           */}
          <Route path="/session/:id/end" element={<SessionEndPage />} />

          {/*
           * Note routes: PendingNotesPage and CompletedNotesPage wrap
           * themselves in DoctorLayout (F-404 pattern, preserved for compat).
           * They are inside RequireAuth but NOT inside DoctorLayoutOutlet to
           * avoid double-wrapping the layout.
           * NoteDecisionPage and NoteEditorPage have their own full-screen
           * layouts and don't use DoctorLayout at all.
           */}
          <Route path="/notes/pending" element={<PendingNotesPage />} />
          <Route path="/notes/completed" element={<CompletedNotesPage />} />
          <Route path="/notes/:noteId/decision" element={<NoteDecisionPage />} />
          <Route path="/notes/:noteId/edit" element={<NoteEditorPage />} />
          <Route path="/notes" element={<Navigate to="/notes/pending" replace />} />
        </Route>

        {/* ================================================================ */}
        {/* P2.5: MFA routes                                                 */}
        {/* /mfa/verify — public (holds only a short-lived mfa-only token)  */}
        {/* /mfa/setup  — requires full doctor auth                          */}
        {/* ================================================================ */}
        <Route path="/mfa/verify" element={<MfaVerifyPage />} />
        <Route element={<RequireAuth role="doctor" />}>
          <Route path="/mfa/setup" element={<MfaSetupPage />} />
        </Route>

        {/* ================================================================ */}
        {/* PATIENT INTERFACE — unauthenticated (QR token auth)              */}
        {/* ================================================================ */}
        <Route element={<PatientLayoutOutlet />}>
          <Route
            path="/join"
            element={<JoinPage />}
          />
          <Route
            path="/session/:id/disclaimer"
            element={<DisclaimerPage />}
          />
          <Route
            path="/session/:id/patient"
            element={<PatientSessionPage />}
          />
        </Route>

        {/* ================================================================ */}
        {/* ADMIN PORTAL — unauthenticated login + MFA flows                 */}
        {/* ================================================================ */}
        {/*
         * F-508: AdminLoginPage (full implementation).
         * Phase 2 Cleanup Item D: MFA verify, setup, and reset pages.
         * All public (no auth required) — they hold only limited-scope tokens.
         */}
        <Route path="/admin/login" element={<AdminLoginPageFull />} />
        <Route path="/admin/mfa/verify" element={<AdminMfaVerifyPage />} />
        <Route path="/admin/mfa/setup" element={<AdminMfaSetupPage />} />
        <Route path="/admin/mfa/reset" element={<AdminMfaResetRequestPage />} />
        <Route path="/admin/mfa/reset/confirm/:token" element={<AdminMfaResetConfirmPage />} />

        {/* ================================================================ */}
        {/* ADMIN PORTAL — authenticated                                      */}
        {/* ================================================================ */}
        {/*
         * F-508 admin pages wrap themselves in AdminLayout (same pattern as F-404
         * note pages with DoctorLayout). They are inside RequireAuth but NOT inside
         * AdminLayoutOutlet to avoid double-wrapping the layout.
         */}
        <Route
          path="/admin"
          element={<RequireAuth role="admin" redirectTo="/admin/login" />}
        >
          <Route index element={<Navigate to="/admin/dashboard" replace />} />

          {/* F-508-FE-02: Dashboard — wraps itself in AdminLayout */}
          <Route path="dashboard" element={<AdminDashboardPage />} />

          {/* F-508-FE-10: Language management — wraps itself in AdminLayout */}
          <Route path="languages" element={<LanguageManagement />} />

          {/* F-508-FE-11: Specialization management — wraps itself in AdminLayout */}
          <Route path="specializations" element={<SpecializationManagement />} />

          {/* Specialization detail page — vocabulary, overview, templates tabs */}
          <Route path="specializations/:id" element={<SpecializationDetailPage />} />

          {/* F-508-FE-12: Template management — wraps itself in AdminLayout */}
          <Route path="templates" element={<TemplateManagement />} />

          {/* STT-Streaming-Frontend: Platform settings — wraps itself in AdminLayout */}
          <Route path="settings" element={<PlatformSettingsPage />} />

          {/* Redirect /admin/hospitals to dashboard (list is on dashboard) */}
          <Route path="hospitals" element={<Navigate to="/admin/dashboard" replace />} />

          {/* F-508-FE-03: Hospital detail with 6 tabs — wraps itself in AdminLayout */}
          <Route path="hospitals/:id" element={<HospitalDetailPage />} />

          {/* Catch-all for admin space */}
          <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
        </Route>

        {/* ================================================================ */}
        {/* Global catch-all                                                  */}
        {/* ================================================================ */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
